import React, { useState } from 'react';
import { Box, Grid, IconButton, Tooltip, useTheme } from '@mui/material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import RepositorySidebar from './components/RepositorySidebar';
import DashboardHeader from './components/DashboardHeader';
import useDashboardData from './hooks/useDashboardData';

import ActionRequiredRow from './dashboard/ActionRequiredRow';
import PipelineHealth from './dashboard/PipelineHealth';
import UpcomingDeadlines from './dashboard/UpcomingDeadlines';
import PortfolioBreakdown from './dashboard/PortfolioBreakdown';
import TopCounterparties from './dashboard/TopCounterparties';
import SignatureMetrics from './dashboard/SignatureMetrics';
import RecentActivityFeed from './dashboard/RecentActivityFeed';

import {
  setActiveWorkspace,
  setActiveFolder,
  setActiveSmartViewAction,
  filterContracts,
} from '../../redux/actions/contractAction';

const CLMDashboardScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const {
    lifecycle,
    smartViewCounts,
    alertSummary,
    deadlines,
    sigPerformance,
    topParties,
  } = useDashboardData();

  // Sidebar filter handler — navigates to contract list with filter
  const handleSidebarFilter = (filterParams) => {
    if (!filterParams) {
      navigate('/contract');
      return;
    }
    if (filterParams.smartView) {
      dispatch(setActiveSmartViewAction(filterParams.smartView));
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
    } else if (filterParams.workspace_id || filterParams.folder_id) {
      dispatch(setActiveWorkspace(filterParams.workspace_id || null));
      dispatch(setActiveFolder(filterParams.folder_id || null));
      dispatch(setActiveSmartViewAction(null));
    }
    dispatch(filterContracts(filterParams, 8, 0));
    navigate('/contract');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      {sidebarOpen && (
        <RepositorySidebar
          onFilterChange={handleSidebarFilter}
          onClose={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            pb: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 2 },
            width: '100%',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!sidebarOpen && (
              <Tooltip title={t('repository.sidebar.open', 'Open sidebar')}>
                <IconButton
                  size="small"
                  onClick={() => setSidebarOpen(true)}
                  sx={{ color: theme.palette.text.secondary, mt: 1 }}
                >
                  <MenuOpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ flex: 1 }}>
              <DashboardHeader title={t('clmDashboard.title', 'Contract Dashboard')} />
            </Box>
          </Box>

          {/* 1. Action Required Row */}
          <Box sx={{ mt: { xs: 0, sm: 2 }, mb: 2.5 }}>
            <ActionRequiredRow
              smartViewCounts={smartViewCounts}
              sigPerformance={sigPerformance}
              alertSummary={alertSummary}
            />
          </Box>

          {/* 2. Pipeline Health */}
          <Box sx={{ mb: 2.5 }}>
            <PipelineHealth lifecycle={lifecycle} />
          </Box>

          {/* 3. Upcoming Deadlines + Signature Metrics */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={8}>
              <UpcomingDeadlines deadlines={deadlines} />
            </Grid>
            <Grid item xs={12} md={4}>
              <SignatureMetrics sigPerformance={sigPerformance} />
            </Grid>
          </Grid>

          {/* 4. Portfolio + Counterparties */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={5}>
              <PortfolioBreakdown lifecycle={lifecycle} />
            </Grid>
            <Grid item xs={12} md={7}>
              <TopCounterparties topParties={topParties} />
            </Grid>
          </Grid>

          {/* 5. Recent Activity */}
          <Box sx={{ mb: 2.5 }}>
            <RecentActivityFeed lifecycle={lifecycle} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CLMDashboardScreen;
